/**
 * Chat Service
 * API calls related to chat and AI queries
 */

import axiosInstance from '../axiosInstance';
import { API_CONFIG } from '../../config/api.config';
import { Chat, ChatMessageItem, QAAnswerResponse, UserChatsResponse, ChatMessagesResponse } from '../../types';

export interface ChatMessage {
  id?: string;
  content: string;
  role: 'user' | 'assistant' | 'system';
  timestamp?: Date;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: Date;
  updatedAt: Date;
}

export interface SendMessageRequest {
  message: string;
  sessionId?: string;
  context?: any;
}

export interface SendMessageResponse {
  id: string;
  content: string;
  sessionId: string;
  timestamp: Date;
}

// New QA Answer Request (for /qa/answer endpoint)
export interface QAAnswerRequest {
  query: string;
  org_id: number;
  user_id: number | string;
  session_id?: string; // Optional, only for subsequent messages
}

// Legacy interfaces (kept for backward compatibility if needed)
export interface QueryRequest {
  question: string;
  max_rows?: number;
  max_columns?: number;
  chat_id: string | null;
}

export interface QueryResponse {
  table?: string;
  response?: string;
  followup_suggestions?: string[];
  total_rows?: number;
  displayed_rows?: number;
  conversation_session_id?: string;
  conversation_history?: any[];
  user_context?: {
    userId: number;
    orgId: number;
    username: string;
  };
  chat_id?: string;
  chat_title?: string;
  is_new_chat?: boolean;
}

/**
 * Send a query to the AI QA endpoint (/qa/answer)
 * Authorization header is automatically sent via axiosInstance.
 */
export const sendQuery = async (
  query: string,
  userId: number | string,
  orgId: number,
  sessionId?: string | null
): Promise<QAAnswerResponse> => {
  console.log('💬 Sending QA query:', {
    endpoint: API_CONFIG.ENDPOINTS.CHAT.QUERY,
    query: query.substring(0, 50) + '...',
    userId,
    orgId,
    sessionId: sessionId || 'new session',
    note: 'Authorization header sent automatically via axiosInstance'
  });

  const requestBody: QAAnswerRequest = {
    query,
    org_id: orgId,
    user_id: userId,
  };

  // Only add session_id if it exists (not for first message)
  if (sessionId) {
    requestBody.session_id = sessionId;
  }

  const response = await axiosInstance.post<QAAnswerResponse>(
    API_CONFIG.ENDPOINTS.CHAT.QUERY,
    requestBody
  );

  console.log('✅ QA query response:', {
    intent: response.data.intent,
    action: response.data.action,
    hasAnswer: !!response.data.answer,
    hasTable: !!response.data.artifacts?.table?.table?.rows?.length,
    hasVisualization: !!response.data.artifacts?.visualization,
    sessionId: response.data.context?.session?.session_id,
    turnIndex: response.data.context?.session?.turn_index,
    followUpCount: response.data.follow_ups?.length || 0,
    latencyMs: response.data.latency_ms,
    cacheHit: response.data.cache_hit
  });

  return response.data;
};

/**
 * Generate a unique session ID
 */
export const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Send chat message (existing functionality)
 */
export const sendMessage = async (data: SendMessageRequest): Promise<SendMessageResponse> => {
  const response = await axiosInstance.post<SendMessageResponse>(
    API_CONFIG.ENDPOINTS.CHAT.SEND,
    data
  );
  return response.data;
};

/**
 * Get chat history
 */
export const getChatHistory = async (sessionId: string): Promise<ChatMessage[]> => {
  const response = await axiosInstance.get<ChatMessage[]>(
    `${API_CONFIG.ENDPOINTS.CHAT.HISTORY}/${sessionId}`
  );
  return response.data;
};

/**
 * Get all chat sessions
 */
export const getChatSessions = async (): Promise<ChatSession[]> => {
  const response = await axiosInstance.get<ChatSession[]>(
    API_CONFIG.ENDPOINTS.CHAT.SESSIONS
  );
  return response.data;
};

/**
 * Delete chat session
 */
export const deleteChatSession = async (sessionId: string): Promise<void> => {
  await axiosInstance.delete(`${API_CONFIG.ENDPOINTS.CHAT.SESSIONS}/${sessionId}`);
};

/**
 * Get user's chats list
 */
export const getUserChats = async (userId: string | number, limit: number = 20): Promise<UserChatsResponse> => {
  console.log('📋 Fetching user chats:', { userId, limit });

  const response = await axiosInstance.get<UserChatsResponse>(
    `${API_CONFIG.ENDPOINTS.CHAT.USER_CHATS(userId.toString())}?limit=${limit}`
  );

  console.log('✅ User chats fetched:', {
    totalChats: response.data.total_chats,
    fetchedCount: response.data.chats.length
  });

  return response.data;
};

/**
 * Get messages for a specific chat
 */
export const getChatMessages = async (chatId: string, limit: number = 100): Promise<ChatMessagesResponse> => {
  console.log('💬 Fetching chat messages:', { chatId, limit });

  const response = await axiosInstance.get<ChatMessagesResponse>(
    `${API_CONFIG.ENDPOINTS.CHAT.CHAT_MESSAGES(chatId)}?limit=${limit}`
  );

  console.log('✅ Chat messages fetched:', {
    chatId: response.data.chat_id,
    messageCount: response.data.message_count,
    fetchedCount: response.data.messages.length
  });

  return response.data;
};

export default {
  sendQuery,
  generateSessionId,
  sendMessage,
  getChatHistory,
  getChatSessions,
  deleteChatSession,
  getUserChats,
  getChatMessages,
};
